import type { TakeScreenshotErrorCode } from "@textclub/common/bridge";

/** Marks the iPhone haptics tap layer, which is inserted right after the canvas it covers. */
export const TAP_LAYER_ATTRIBUTE = "data-jest-haptics";

const GAME_CANVAS_MARKERS = [
  "gamecanvas",
  "game-canvas",
  "unity-canvas",
  "godot-canvas",
];

// ~1.5s backoff window so engines that redraw only occasionally still get sampled.
const CAPTURE_RETRY_DELAYS_MS = [0, 50, 100, 200, 400, 800];
const BLANK_SAMPLE_SIZE = 16;

export type CaptureResult =
  | { success: true; image: string }
  | { success: false; error: TakeScreenshotErrorCode };

type CanvasSample = "transparent" | "solid" | "content";

/**
 * Captures the main canvas inside a requestAnimationFrame callback. Engines
 * like Unity and Cocos create their WebGL context with
 * preserveDrawingBuffer:false (the default), so the drawing buffer is only
 * readable between the engine's render and the frame's composite. The
 * engine's own rAF callback was registered before ours (during the previous
 * frame), so it renders first and our read lands in that window; a
 * synchronous read from the postMessage handler would see an invalidated
 * buffer — black or fully transparent.
 *
 * A blank-looking capture retries on later frames with backoff (the engine
 * may not be drawing). Once attempts run out, a fully transparent frame fails
 * with "blank" since no real game frame looks like that, while a solid opaque
 * frame is returned as-is because a game can legitimately display one.
 */
export function captureMainCanvas(
  doc: Document,
  onDone: (result: CaptureResult) => void,
): void {
  const onFrame: (cb: () => void) => void =
    typeof requestAnimationFrame === "function"
      ? (cb) => requestAnimationFrame(() => cb())
      : (cb) => cb();

  const attempt = (index: number) => {
    const delay = CAPTURE_RETRY_DELAYS_MS[index] ?? 0;
    const schedule =
      delay > 0
        ? (cb: () => void) => setTimeout(() => onFrame(cb), delay)
        : onFrame;

    schedule(() => {
      const canvas = findMainCanvas(doc);
      if (!canvas) {
        onDone({ success: false, error: "not_available" });
        return;
      }

      const isLastAttempt = index >= CAPTURE_RETRY_DELAYS_MS.length - 1;
      const sample = sampleCanvas(canvas);
      if (sample !== "content" && !isLastAttempt) {
        attempt(index + 1);
        return;
      }
      if (sample === "transparent") {
        onDone({ success: false, error: "blank" });
        return;
      }

      try {
        // Throws on tainted canvases.
        onDone({ success: true, image: canvas.toDataURL("image/png") });
      } catch {
        onDone({ success: false, error: "capture_failed" });
      }
    });
  };

  attempt(0);
}

// Must run in the capture's frame; an invalidated WebGL buffer reads uniform, usually transparent.
function sampleCanvas(canvas: HTMLCanvasElement): CanvasSample {
  try {
    const sample = canvas.ownerDocument.createElement("canvas");
    sample.width = BLANK_SAMPLE_SIZE;
    sample.height = BLANK_SAMPLE_SIZE;
    const context = sample.getContext("2d", { willReadFrequently: true });
    if (!context) {
      return "content";
    }

    context.drawImage(canvas, 0, 0, BLANK_SAMPLE_SIZE, BLANK_SAMPLE_SIZE);
    const { data } = context.getImageData(
      0,
      0,
      BLANK_SAMPLE_SIZE,
      BLANK_SAMPLE_SIZE,
    );

    for (let i = 4; i < data.length; i += 4) {
      if (
        data[i] !== data[0] ||
        data[i + 1] !== data[1] ||
        data[i + 2] !== data[2] ||
        data[i + 3] !== data[3]
      ) {
        return "content";
      }
    }
    return data[3] === 0 ? "transparent" : "solid";
  } catch {
    return "content";
  }
}

/**
 * Picks the game's "main" canvas. Known engine canvas markers win among
 * visible on-screen canvases, then area decides. Only when no canvas is laid
 * out at all does intrinsic size decide, so a hidden buffer/atlas canvas can
 * never shadow a visible one.
 */
export function findMainCanvas(doc: Document): HTMLCanvasElement | null {
  const canvases = Array.from(doc.querySelectorAll("canvas"));
  const visibleCanvases = canvases.filter(isCanvasStyleVisible);
  if (visibleCanvases.length === 1) {
    const [canvas] = visibleCanvases;
    return canvas && isSingleVisibleCanvasCandidate(canvas) ? canvas : null;
  }

  const viewportCandidates = visibleCanvases.filter(
    (canvas) => getViewportArea(canvas) > 0,
  );

  const markedViewportCandidate = largestBy(
    viewportCandidates.filter((canvas) => getGameCanvasMarkerScore(canvas) > 0),
    (canvas) =>
      getGameCanvasMarkerScore(canvas) * 1_000_000 + getViewportArea(canvas),
  );

  if (markedViewportCandidate) {
    return markedViewportCandidate;
  }

  const hitTestedViewportCandidate =
    getBestHitTestedViewportCandidate(viewportCandidates);

  if (hitTestedViewportCandidate) {
    return hitTestedViewportCandidate;
  }

  return largestBy(
    visibleCanvases.filter((canvas) => {
      const rect = canvas.getBoundingClientRect();
      return rect.width === 0 && rect.height === 0;
    }),
    (canvas) => canvas.width * canvas.height,
  );
}

function isSingleVisibleCanvasCandidate(canvas: HTMLCanvasElement): boolean {
  if (getViewportArea(canvas) > 0) {
    return true;
  }

  const rect = canvas.getBoundingClientRect();
  return (
    rect.width === 0 && rect.height === 0 && canvas.width * canvas.height > 0
  );
}

function getGameCanvasMarkerScore(canvas: HTMLCanvasElement): number {
  const exactId = canvas.id.toLowerCase();
  if (GAME_CANVAS_MARKERS.includes(exactId)) {
    return 3;
  }

  const classNames = Array.from(canvas.classList, (className) =>
    className.toLowerCase(),
  );
  if (classNames.some((className) => GAME_CANVAS_MARKERS.includes(className))) {
    return 2;
  }

  const name = `${exactId} ${classNames.join(" ")}`;
  return GAME_CANVAS_MARKERS.some((marker) => name.includes(marker)) ? 1 : 0;
}

function getCanvasViewportHitScore(canvas: HTMLCanvasElement): number {
  const doc = canvas.ownerDocument;
  if (typeof doc.elementFromPoint !== "function") {
    return 0;
  }

  const rect = canvas.getBoundingClientRect();
  const view = doc.defaultView;
  const viewportWidth = view?.innerWidth ?? doc.documentElement.clientWidth;
  const viewportHeight = view?.innerHeight ?? doc.documentElement.clientHeight;
  const left = Math.max(rect.left, 0);
  const top = Math.max(rect.top, 0);
  const right = Math.min(rect.right, viewportWidth);
  const bottom = Math.min(rect.bottom, viewportHeight);
  if (right <= left || bottom <= top) {
    return 0;
  }

  const samplePoints = [
    [0.5, 0.5],
    [0.25, 0.25],
    [0.75, 0.25],
    [0.25, 0.75],
    [0.75, 0.75],
  ] as const;

  let hits = 0;
  for (const [xRatio, yRatio] of samplePoints) {
    const x = left + (right - left) * xRatio;
    const y = top + (bottom - top) * yRatio;
    if (hitsCanvas(doc.elementFromPoint(x, y), canvas)) {
      hits += 1;
    }
  }

  return hits;
}

// The iPhone haptics layer sits invisibly over its canvas, so a hit on it is a hit on the canvas.
function hitsCanvas(hit: Element | null, canvas: HTMLCanvasElement): boolean {
  return (
    hit === canvas ||
    hit?.closest(`[${TAP_LAYER_ATTRIBUTE}]`)?.previousElementSibling === canvas
  );
}

function getBestHitTestedViewportCandidate(
  canvases: HTMLCanvasElement[],
): HTMLCanvasElement | null {
  let best: HTMLCanvasElement | null = null;
  let bestHits = 0;
  let bestArea = 0;

  for (const canvas of canvases) {
    const hits = getCanvasViewportHitScore(canvas);
    const area = getViewportArea(canvas);
    if (!best || hits > bestHits || (hits === bestHits && area > bestArea)) {
      best = canvas;
      bestHits = hits;
      bestArea = area;
    }
  }

  return best;
}

export function isCanvasStyleVisible(canvas: HTMLCanvasElement): boolean {
  const view = canvas.ownerDocument.defaultView;
  if (!view) {
    return true;
  }

  const style = view.getComputedStyle(canvas);
  if (style.display === "none" || style.visibility === "hidden") {
    return false;
  }

  // Neither display nor opacity is inherited, so check every ancestor too.
  for (let el: HTMLElement | null = canvas; el; el = el.parentElement) {
    const ancestorStyle = view.getComputedStyle(el);
    if (ancestorStyle.display === "none" || ancestorStyle.opacity === "0") {
      return false;
    }
  }

  return true;
}

function getViewportArea(canvas: HTMLCanvasElement): number {
  const rect = canvas.getBoundingClientRect();
  const view = canvas.ownerDocument.defaultView;
  const viewportWidth =
    view?.innerWidth ?? canvas.ownerDocument.documentElement.clientWidth;
  const viewportHeight =
    view?.innerHeight ?? canvas.ownerDocument.documentElement.clientHeight;
  const width = Math.max(
    0,
    Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0),
  );
  const height = Math.max(
    0,
    Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0),
  );

  return width * height;
}

function largestBy(
  canvases: HTMLCanvasElement[],
  area: (canvas: HTMLCanvasElement) => number,
): HTMLCanvasElement | null {
  let best: HTMLCanvasElement | null = null;
  let bestArea = 0;

  for (const canvas of canvases) {
    const canvasArea = area(canvas);
    if (canvasArea > bestArea) {
      best = canvas;
      bestArea = canvasArea;
    }
  }

  return best;
}
